import React, { useEffect, useState } from 'react'
import {
  Icon,
  Card,
  Field,
  FieldGroup,
  Label,
  RadioGroup,
  Radio,
  Input,
  Select,
  Button,
} from '../../../../atoms'
import { getComponentById } from '@r/component/component-selectors'
import { connect } from 'react-redux'
import { FsCore } from '@t/fs-core'
import { getBestRate } from './calculation'
import ImgIx from '../../../imgix-img'
import TravelOther from './travel-other'
import ProgressCircle from '@uswitch/ev-components/lib/components/elements/progress-circle'
import { timeout, useComponentTracking } from '@uswitch/ev-utils/component-events'
import {
  currencyMappings,
  currencyTypeMappings,
  gbpCurrency,
  mainCurrencyOptions,
  flagMappings,
} from './mappings'
import ErrorMessage from './error-message'
import classNames from 'classnames'

export type Collection = 'delivery' | 'collection'
interface BestRate {
  rate: number
  collection: boolean
  delivery: boolean
  deliveryRate: string | number
  logo: string
  name: string
  amountReceived: number
  error?: string
}

export const formatThousands = (n: string | number) =>
  n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const TravelMoneyCalculator = ({ travelMoneyData }: { travelMoneyData: any }) => {
  const [currencyType, setCurrencyType] = useState('cash')
  const [fromCurrency, setFromCurrency] = useState('GBP')
  const [mainCurrency, setMainCurrency] = useState('EUR')
  const [swapped, setSwapped] = useState(false)
  const [amount, setAmount] = useState(750)
  const [collectionType, setCollectionType] = useState<Collection>('delivery')
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [bestRate, setBestRate] = useState<BestRate | null>(null)
  const trackComponent = useComponentTracking()

  useEffect(() => {
    if (amount > 0 && (fromCurrency === 'GBP' ? amount <= 7500 : amount <= 999999999)) {
      setError(false)
      setLoading(true)
      setTimeout(() => {
        const rate: BestRate = getBestRate(
          amount,
          amount,
          fromCurrency,
          swapped ? fromCurrency : mainCurrency,
          collectionType,
          travelMoneyData,
        )
        setBestRate(rate)
        setLoading(false)
      }, 500)
    } else {
      setError(true)
    }
  }, [amount, fromCurrency, mainCurrency, swapped, collectionType, travelMoneyData])

  const amountReceived =
    bestRate?.amountReceived &&
    (fromCurrency === 'GBP'
      ? bestRate.amountReceived.toFixed(2)
      : (0 - bestRate.amountReceived).toFixed(2))

  return (
    <div className='TravelMoneyCalculator'>
      <Card variant='plum'>
        <h2 className='TravelMoneyCalculator__title'>Currency Calculator</h2>
        <div className='TravelMoneyCalculator__inner-card'>
          <Label>How would you like your currency?</Label>
          <RadioGroup stack={true}>
            <Radio
              value='cash'
              name='cash'
              currentValue={currencyType}
              onChange={(e: any) => {
                trackComponent('Dynamic Component Event', 'ChangeCurrencyCalculatorCurrencyType', {
                  type: 'cash',
                })
                setCurrencyType(e.target.value)
              }}
              centerLabel={true}
              buttonAlignment={'top-left'}
            >
              <div className='TravelMoneyCalculator__currencyType'>
                <Icon glyph={'cash'} size={32} color={'#34454E'} />
                <div className='TravelMoneyCalculator__currencyType__text'>
                  <span className='TravelMoneyCalculator__currencyType__label'>Cash</span>
                </div>
              </div>
            </Radio>
            <Radio
              value='money-travel-card'
              name='money-travel-card'
              currentValue={currencyType}
              onChange={(e: any) => {
                trackComponent('Dynamic Component Event', 'ChangeCurrencyCalculatorCurrencyType', {
                  type: 'money-travel-card',
                })
                setCurrencyType(e.target.value)
              }}
              centerLabel={true}
              buttonAlignment={'top-left'}
            >
              <div className='TravelMoneyCalculator__currencyType'>
                <Icon glyph={'card-fees'} size={32} color={'#34454E'} />
                <div className='TravelMoneyCalculator__currencyType__text'>
                  <span className='TravelMoneyCalculator__currencyType__label'>
                    Travel money card
                  </span>
                </div>
              </div>
            </Radio>
            <Radio
              value='travel-insurance'
              name='travel-insurance'
              currentValue={currencyType}
              onChange={(e: any) => {
                trackComponent('Dynamic Component Event', 'ChangeCurrencyCalculatorCurrencyType', {
                  type: 'travel-insurance',
                })
                setCurrencyType(e.target.value)
              }}
              centerLabel={true}
              buttonAlignment={'top-left'}
            >
              <div className='TravelMoneyCalculator__currencyType'>
                <Icon glyph={'travel-insurance'} size={32} color={'#34454E'} />
                <div className='TravelMoneyCalculator__currencyType__text'>
                  <span className='TravelMoneyCalculator__currencyType__label'>
                    Travel Insurance
                  </span>
                </div>
              </div>
            </Radio>
            <Radio
              value='prepaid-card'
              name='prepaid-card'
              currentValue={currencyType}
              onChange={(e: any) => {
                trackComponent('Dynamic Component Event', 'ChangeCurrencyCalculatorCurrencyType', {
                  type: 'prepaid-card',
                })
                setCurrencyType(e.target.value)
              }}
              centerLabel={true}
              buttonAlignment={'top-left'}
            >
              <div className='TravelMoneyCalculator__currencyType'>
                <Icon glyph={'prepaid-card'} size={32} color={'#34454E'} />
                <div className='TravelMoneyCalculator__currencyType__text'>
                  <span className='TravelMoneyCalculator__currencyType__label'>Prepaid card</span>
                </div>
              </div>
            </Radio>
          </RadioGroup>
        </div>
        {currencyType === 'cash' ? (
          <>
            <FieldGroup stack={true}>
              <Field stack={true}>
                <Label>Amount</Label>
                <Input
                  name='amount'
                  value={amount}
                  onChange={(e: any) => {
                    trackComponent('Dynamic Component Event', 'SetCurrencyCalculatorInputAmount', {
                      amount: e.target.value,
                    })
                    setAmount(e.target.value)
                  }}
                  type='text'
                  prefix={currencyMappings.find((c) => c.code === fromCurrency)?.symbol ?? ''}
                  format='thousands'
                  error={error}
                />
                {error && (
                  <div className='TravelMoneyCalculator__error'>
                    Min order amount £1, max £7,500
                  </div>
                )}
              </Field>
              <Field stack={true}>
                <Label>From</Label>
                <Select
                  name='from-currency'
                  value={fromCurrency}
                  options={swapped ? mainCurrencyOptions : gbpCurrency}
                  onChange={(e: any) => {
                    trackComponent('Dynamic Component Event', 'SetCurrencyCalculatorFromCurrency', {
                      amount: e.target.value,
                    })
                    setFromCurrency(e.target.value)
                  }}
                  prefixIcon={flagMappings[fromCurrency]}
                />
              </Field>
              <div className='TravelMoneyCalculator__amountField'>
                <button
                  className={classNames('TravelMoneyCalculator__swapButton', {
                    'TravelMoneyCalculator__swapButton--error': error,
                  })}
                  onClick={() => {
                    trackComponent('Dynamic Component Event', 'SwapCurrencyCalculatorCurrencies', {
                      from: mainCurrency,
                      to: fromCurrency,
                    })
                    setSwapped(!swapped)
                    setAmount(1000)
                    setFromCurrency(mainCurrency)
                    setMainCurrency(fromCurrency)
                  }}
                >
                  <Icon glyph={'swap-circle'} size={32} color={'#34454E'} />
                </button>
              </div>
              <Field stack={true}>
                <Label>To</Label>
                <Select
                  name='to-currency'
                  value={mainCurrency}
                  options={swapped ? gbpCurrency : mainCurrencyOptions}
                  onChange={(e: any) => {
                    trackComponent('Dynamic Component Event', 'SetCurrencyCalculatorToCurrency', {
                      amount: e.target.value,
                    })
                    setMainCurrency(e.target.value)
                  }}
                  prefixIcon={flagMappings[mainCurrency]}
                />
              </Field>
            </FieldGroup>
            <div className='TravelMoneyCalculator__inner-card'>
              <Label>Delivery or Collection?</Label>
              <div className='TravelMoneyCalculator__collectionContainer'>
                <RadioGroup stack={true}>
                  <Radio
                    value='delivery'
                    name='delivery'
                    currentValue={collectionType}
                    onChange={(e: any) => {
                      trackComponent(
                        'Dynamic Component Event',
                        'SetCurrencyCalculatorDeliveryOrCollection',
                        { type: 'delivery' },
                      )
                      setCollectionType(e.target.value)
                    }}
                    centerLabel={true}
                    buttonAlignment={'top-left'}
                  >
                    <div className='TravelMoneyCalculator__currencyType'>
                      <Icon glyph={'delivery'} size={32} color={'#34454E'} />
                      <div className='TravelMoneyCalculator__currencyType__text'>
                        <span className='TravelMoneyCalculator__currencyType__label'>Delivery</span>
                      </div>
                    </div>
                  </Radio>
                  <Radio
                    value='collection'
                    name='collection'
                    currentValue={collectionType}
                    onChange={(e: any) => {
                      trackComponent(
                        'Dynamic Component Event',
                        'SetCurrencyCalculatorDeliveryOrCollection',
                        { type: 'collection' },
                      )
                      setCollectionType(e.target.value)
                    }}
                    centerLabel={true}
                    buttonAlignment={'top-left'}
                  >
                    <div className='TravelMoneyCalculator__currencyType'>
                      <Icon glyph={'location'} size={32} color={'#34454E'} />
                      <div className='TravelMoneyCalculator__currencyType__text'>
                        <span className='TravelMoneyCalculator__currencyType__label'>
                          Collection
                        </span>
                      </div>
                    </div>
                  </Radio>
                </RadioGroup>
              </div>
            </div>
            <div className='TravelMoneyCalculator__conversionData'>
              {loading ? (
                <div className='TravelMoneyCalculator__loadingSpinner'>
                  <ProgressCircle sx={{ maxHeight: '32px' }} />
                </div>
              ) : bestRate && !error ? (
                bestRate.error ? (
                  <ErrorMessage error={bestRate.error} />
                ) : (
                  <>
                    <div className='TravelMoneyCalculator__conversionData__exchange'>
                      <div className='TravelMoneyCalculator__conversionData__exchange__bestRate'>
                        Best Rate from
                      </div>
                      <div className='TravelMoneyCalculator__conversionData__logo'>
                        <ImgIx
                          src={bestRate.logo}
                          width={150}
                          alt={`${bestRate.name} logo`}
                          critical={false}
                        />
                      </div>
                      <div className='TravelMoneyCalculator__conversionData__text'>
                        <div>
                          1 {fromCurrency} ={' '}
                          {swapped ? (1 / bestRate.rate).toFixed(3) : bestRate.rate} {mainCurrency}
                        </div>
                        <div>
                          1 {mainCurrency} ={' '}
                          {swapped ? bestRate.rate : (1 / bestRate.rate).toFixed(3)} {fromCurrency}
                        </div>
                      </div>
                    </div>
                    <div className='TravelMoneyCalculator__conversionData__disclaimer'>
                      {`Amount received ${
                        collectionType === 'delivery' ? '(after accounting for delivery fees)' : ''
                      }`}
                    </div>
                    <div className='TravelMoneyCalculator__conversionData__received'>
                      <span className='TravelMoneyCalculator__conversionData__received__left'>
                        {formatThousands(amount)}{' '}
                        {currencyMappings.find((c) => c.code === fromCurrency)?.code} ={' '}
                      </span>
                      <span className='TravelMoneyCalculator__conversionData__received__right'>
                        {amountReceived && formatThousands(amountReceived)}{' '}
                        {currencyMappings.find((c) => c.code === mainCurrency)?.code}
                      </span>
                    </div>
                  </>
                )
              ) : (
                <div className='TravelMoneyCalculator__errorState'>
                  There are no results matching your search, please try again.
                </div>
              )}
            </div>
            <div className='TravelMoneyCalculator__cta'>
              <Button
                variant={'primary'}
                onClick={() => {
                  const eventData =
                    currencyType === 'cash'
                      ? {
                          currencyType,
                          amount,
                          fromCurrency,
                          toCurrency: mainCurrency,
                          collectionType,
                        }
                      : { currencyType }
                  timeout(
                    trackComponent(
                      'Dynamic Component Event',
                      'CurrencyCalculatorShowResults',
                      eventData,
                    ),
                  ).then(() => {
                    window.location.href = `/travel-money/results?gbpAmount=${amount}&currencyToUse=GBP&currencyPair=${
                      swapped ? fromCurrency : mainCurrency
                    }&fromCurrency=GBP&mainCurrency=${
                      swapped ? fromCurrency : mainCurrency
                    }&otherAmount=${amount}&collectionType=${collectionType}&swapped=${swapped}`
                  })
                }}
                withIcon={false}
              >
                Show Results
              </Button>
            </div>
          </>
        ) : (
          <TravelOther
            text={currencyTypeMappings[currencyType].text}
            buttonText={currencyTypeMappings[currencyType].buttonText}
            href={currencyTypeMappings[currencyType].href}
          />
        )}
      </Card>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    travelMoneyData: getComponentById(state, props.sys.id)?.travelMoneyData,
    ...props,
  }
})(TravelMoneyCalculator)
